import type { Tag } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import { createTag, destroyTag, updateTag } from "./api"

export function useCreateTagMutation() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (tag: Partial<Tag>) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createTag(context, tag)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] })
    },
  })
}

export function useUpdateTagMutation() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, tag }: { id: string; tag: Partial<Tag> }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateTag(context, id, tag)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] })
    },
  })
}

export function useDestroyTagMutation() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (tag: Tag) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return destroyTag(context, tag)
    },
    onSuccess: (_, deletedTag) => {
      queryClient.invalidateQueries({ queryKey: ["tags"], refetchType: "all" })
      if (deletedTag.entity_type === "opportunity") {
        queryClient.removeQueries({ queryKey: ["pipeline"] })
        queryClient.removeQueries({ queryKey: ["household"] })
      }
      if (deletedTag.entity_type === "task") {
        queryClient.removeQueries({ queryKey: ["tasks"] })
      }
    },
  })
}
